import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { Separator } from "@/components/ui/separator";
import type { LabelField, LabelSettings } from "../types";
import { calculateBarcodeWidthForDPI } from "../utils/barcodeUtils";
import { calculateTextDimensions } from "../utils/textUtils";
import { generateZPL } from "../utils/zplGenerator";
import { BarcodeSettings } from "./BarcodeSettings";
import { FieldPalette } from "./FieldPalette";
import { LabelCanvas } from "./LabelCanvas";
import { LabelDimensions } from "./LabelDimensions";
import { LabelPreview } from "./LabelPreview";

export function LabelDesigner() {
  const [labelSettings, setLabelSettings] = useState<LabelSettings>({
    width: 456, // Default Zebra label width in dots
    height: 152, // Default height
    dpi: 203, // Standard Zebra DPI
    marginTop: 8, // Minimum top margin in dots
    marginLeft: 8, // Minimum left margin in dots
    marginRight: 8, // Minimum right margin in dots
    marginBottom: 8, // Minimum bottom margin in dots
  });

  const [fields, setFields] = useState<LabelField[]>([]);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  const handleAddField = (type: LabelField["type"]) => {
    // Start fields within the printable area
    const marginLeft = labelSettings.marginLeft || 8;
    const marginTop = labelSettings.marginTop || 8;

    // Calculate accurate dimensions for barcodes
    const defaultContent = type === "text" ? "Sample Text" : "123456789";
    let fieldWidth = type === "barcode" ? 200 : 150;
    let fieldHeight = type === "barcode" ? 50 : 20;

    if (type === "barcode") {
      const calculation = calculateBarcodeWidthForDPI(
        defaultContent,
        labelSettings.dpi,
        0.0075, // min X dimension
        50, // barcode height
        true, // show text
        12, // font size
      );
      fieldWidth = calculation.widthInDots;
      fieldHeight = calculation.heightInDots;
    }

    const newField: LabelField = {
      id: `field-${Date.now()}`,
      type,
      x: marginLeft + 10, // Start inside margins with some padding
      y: marginTop + 10,
      width: fieldWidth,
      height: fieldHeight,
      content: defaultContent,
      fontSize: 20,
      fontFamily: "0",
      alignment: "L",
      ...(type === "barcode" && {
        barcodeType: "BCN", // Code 128 only for now
        barcodeHeight: 50,
        showText: true,
      }),
    };
    setFields([...fields, newField]);
  };

  const handleUpdateField = (id: string, updates: Partial<LabelField>) => {
    setFields(
      fields.map((field) => {
        if (field.id !== id) return field;

        const updatedField = { ...field, ...updates };

        // Auto-update text field height when content, font size, font family, or wrapping changes
        if (
          updatedField.type === "text" &&
          (updates.content !== undefined ||
            updates.fontSize !== undefined ||
            updates.fontFamily !== undefined ||
            updates.width !== undefined)
        ) {
          const textCalc = calculateTextDimensions(
            updatedField.content,
            updatedField.fontSize || 20,
            updatedField.fontFamily || "0",
            updatedField.width,
          );
          updatedField.height = textCalc.heightInDots;
        }

        return updatedField;
      }),
    );
  };

  const handleDeleteField = (id: string) => {
    setFields(fields.filter((field) => field.id !== id));
    if (selectedField === id) {
      setSelectedField(null);
    }
  };

  const handlePrint = async () => {
    try {
      // Generate ZPL from current design
      const zpl = generateZPL(labelSettings, fields);

      // Use BrowserPrint directly like in zebra.ts
      // biome-ignore lint/suspicious/noExplicitAny: need typings
      const BrowserPrint = (window as any).BrowserPrint;

      if (!BrowserPrint) {
        throw new Error(
          "BrowserPrint not available. Please install Zebra Browser Print.",
        );
      }

      await new Promise<void>((resolve, reject) => {
        BrowserPrint.getDefaultDevice(
          "printer",
          // biome-ignore lint/suspicious/noExplicitAny: need typings
          (device: any) => {
            device.send(zpl, resolve, reject);
            resolve();
          },
          (error: unknown) => {
            reject(error);
          },
        );
      });
    } catch (error) {
      console.error("Failed to print label:", error);
      alert(
        "Failed to print label. Please ensure Zebra Browser Print is installed and a printer is connected.",
      );
    }
  };

  return (
    <div className="flex h-full">
      {/* Left Panel - Controls */}
      <div className="w-80 border-r bg-background p-4 space-y-6 overflow-y-auto">
        <div>
          <Heading level={2} className="mb-4">
            Label Designer
          </Heading>
          <Separator />
        </div>

        <LabelDimensions settings={labelSettings} onChange={setLabelSettings} />

        <FieldPalette onAddField={handleAddField} />

        <BarcodeSettings
          selectedField={
            selectedField
              ? fields.find((f) => f.id === selectedField) || null
              : null
          }
          onUpdateField={handleUpdateField}
          settings={labelSettings}
        />

        <div className="space-y-2">
          <Button onPress={handlePrint} className="w-full" variant="default">
            Print Label
          </Button>
          <Button
            onPress={() => setFields([])}
            className="w-full"
            variant="outline"
          >
            Clear All
          </Button>
        </div>
      </div>

      {/* Center Panel - Canvas */}
      <div className="flex-1 p-4">
        <Card className="h-full">
          <LabelCanvas
            settings={labelSettings}
            fields={fields}
            selectedField={selectedField}
            onSelectField={setSelectedField}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
          />
        </Card>
      </div>

      {/* Right Panel - Preview */}
      <div className="w-80 border-l bg-background p-4">
        <LabelPreview settings={labelSettings} fields={fields} />
      </div>
    </div>
  );
}
